import { DetailForm } from '@/@diffrag/form/DetailForm'
import { FormFieldType } from '@/@diffrag/form/fields/type'
import { useApi } from '@/@diffrag/hooks/useApi'
import { useQueryValue } from '@/@diffrag/hooks/useQueryValue'
import { ContentDetailView } from '@/@diffrag/layout/ContentDetailView'
import { api } from '@/api'
import {
  IAdminClinicNewEquipmentRO,
  IAdminClinicConfirmNewEquipmentRequest,
  IAdminClinicConfirmNewEquipmentRequestStatusEnum,
  IAdminClinicConfirmDoctorProfileRequest,
} from 'api'
import { useNavigate } from 'react-router-dom'
import parse from 'html-react-parser'

export default function NewEquipmentEdit() {
  const navigate = useNavigate()
  const id = useQueryValue('id', '')

  const { data, refetch } = useApi({
    id: 'equipmentDetail',
    fn: api().adminClinicNewEquipment.adminClinicNewEquipmentDetail,
    params: Number(id),
    enabled: !!id,
  })

  const onSave = async (data: IAdminClinicNewEquipmentRO) => {
    if (id) {
      const updateData: IAdminClinicConfirmNewEquipmentRequest = {
        status:
          data.status as unknown as IAdminClinicConfirmNewEquipmentRequestStatusEnum,
        rejectReason: data.rejectReason || '',
      }

      await api().adminClinicNewEquipment.approveClinicNewEquipment(
        Number(id),
        updateData as unknown as IAdminClinicConfirmDoctorProfileRequest,
      )
    }

    refetch()

    navigate(-1)
  }

  return (
    <ContentDetailView title="신규 장비 승인" data={data} onSave={onSave}>
      <DetailForm
        data={data}
        fields={[
          {
            label: '병원명',
            key: 'clinicName',
            type: FormFieldType.STRING,
            readOnly: true,
          },
          {
            label: '장비명',
            key: 'equipmentName',
            type: FormFieldType.CUSTOM_READ_ONLY,
            render: (data) => {
              if (!data) return <span>장비명이 없습니다.</span>

              // 언어별 장비명 매핑
              const languageMap: Record<string, string> = {
                ko: '한국어',
                'zh-CN': '简体中文',
                'zh-TW': '繁體中文',
                en: 'English',
                ja: '日本語',
                th: 'ไทย',
              }

              const equipmentNames = [
                { lang: 'ko', name: data.equipmentName, label: '한국어' },
                {
                  lang: 'zh-CN',
                  name: data.equipmentNameZhcn,
                  label: '简体中文',
                },
                {
                  lang: 'zh-TW',
                  name: data.equipmentNameZhtw,
                  label: '繁體中文',
                },
                { lang: 'en', name: data.equipmentNameEn, label: 'English' },
                { lang: 'ja', name: data.equipmentNameJa, label: '日本語' },
                { lang: 'th', name: data.equipmentNameTh, label: 'ไทย' },
              ].filter((item) => item.name) // 값이 있는 것만 필터링

              return (
                <div className="space-y-2">
                  {equipmentNames.map((item) => (
                    <div
                      key={item.lang}
                      className="rounded border bg-gray-50 p-3"
                    >
                      <div className="text-sm font-semibold text-blue-600">
                        {item.label}
                      </div>
                      <div className="mt-1 text-sm text-gray-700">
                        {item.name}
                      </div>
                    </div>
                  ))}
                </div>
              )
            },
          },
          {
            label: '장비 이미지',
            key: 'images',
            type: FormFieldType.CUSTOM_READ_ONLY,
            render: (data) => {
              if (!data?.images || data.images.length === 0) {
                return <span>이미지가 없습니다.</span>
              }
              return (
                <div className="grid grid-cols-2 gap-4">
                  {data.images.map((item, index) => (
                    <div key={index} className="space-y-2">
                      <img
                        src={item.image.thumbnailPath ?? item.image.path}
                        alt={`장비 이미지 ${index + 1}`}
                        className="h-48 w-full rounded border object-cover"
                      />
                      <div className="text-xs text-gray-500">
                        {item.isMain && (
                          <span className="mr-2 rounded bg-blue-100 px-2 py-1 text-xs text-blue-800">
                            메인
                          </span>
                        )}
                        순서: {item.order}
                      </div>
                    </div>
                  ))}
                </div>
              )
            },
          },
          {
            label: '상세내용',
            key: 'content',
            type: FormFieldType.CUSTOM_READ_ONLY,
            readOnly: true,
            render(data) {
              if (!data) return <span>상세내용이 없습니다.</span>

              const contents = [
                { lang: 'ko', content: data.content, label: '한국어' },
                { lang: 'zh-CN', content: data.contentZhcn, label: '简体中文' },
                { lang: 'zh-TW', content: data.contentZhtw, label: '繁體中文' },
                { lang: 'en', content: data.contentEn, label: 'English' },
                { lang: 'ja', content: data.contentJa, label: '日本語' },
                { lang: 'th', content: data.contentTh, label: 'ไทย' },
              ].filter((item) => item.content) // 값이 있는 것만 필터링

              return (
                <div className="space-y-4">
                  {contents.map((item) => (
                    <div key={item.lang} className="space-y-2">
                      <div className="border-b border-blue-200 pb-2 text-lg font-bold text-blue-600">
                        {item.label}
                      </div>
                      <div className="min-h-[150px] w-full rounded border bg-gray-50 p-3">
                        {parse(item.content ?? '')}
                      </div>
                    </div>
                  ))}
                </div>
              )
            },
          },
          {
            label: '등록일',
            key: 'createDate',
            type: FormFieldType.DATE,
            readOnly: true,
          },
          {
            label: '상태',
            key: 'status',
            type: FormFieldType.SELECT,
            options: [
              { label: '대기', value: 'PENDING' },
              { label: '승인', value: 'APPROVED' },
              { label: '반려', value: 'REJECTED' },
            ],
            required: true,
          },
          {
            label: '거절사유',
            key: 'rejectReason',
            type: FormFieldType.TEXT,
          },
          {
            label: '승인일',
            key: 'approveDate',
            type: FormFieldType.DATE,
            readOnly: true,
          },
          {
            label: '거절일',
            key: 'rejectDate',
            type: FormFieldType.DATE,
            readOnly: true,
          },
        ]}
      />
    </ContentDetailView>
  )
}
